// ---------------------------------------------------------------------------
// Tool detail source and response shapes.
//
// A value's presence is deliberately modeled separately from its value: null,
// an empty string, {}, and 0 are all valid details rather than aliases for an
// unavailable legacy field.
// ---------------------------------------------------------------------------

export const TOOL_DETAIL_FIELD_STATES = [
  "available",
  "unavailable",
  "malformed",
  "truncated",
] as const;

export type ToolDetailFieldState = (typeof TOOL_DETAIL_FIELD_STATES)[number];

export interface ToolDetailField {
  state: ToolDetailFieldState;
  value?: unknown;
}

export interface ToolDetails {
  parameters: ToolDetailField;
  result: ToolDetailField;
  error: ToolDetailField;
}

export type LiveToolDetailPatch = Partial<ToolDetails>;

export interface LiveToolDetailSource {
  kind: "live";
  /** Local render identity only; never a persisted/API key. */
  key: string;
  details: ToolDetails;
}

export interface HistoryToolDetailSource {
  kind: "history";
  sessionId: string;
  messageId: string;
}

/** A legacy/malformed row with no safe detail lookup identity. */
export interface UnavailableToolDetailSource {
  kind: "unavailable";
  key: string;
}

export type ToolDetailSource =
  | LiveToolDetailSource
  | HistoryToolDetailSource
  | UnavailableToolDetailSource;

export interface ToolDetailsResponse extends ToolDetails {
  message_id: string;
  tool_name: string;
}

export function unavailableToolDetails(): ToolDetails {
  return {
    parameters: { state: "unavailable" },
    result: { state: "unavailable" },
    error: { state: "unavailable" },
  };
}

export function mergeLiveToolDetails(
  current: ToolDetails | undefined,
  patch: LiveToolDetailPatch | undefined,
): ToolDetails {
  return {
    ...unavailableToolDetails(),
    ...current,
    ...patch,
  };
}

function isDetailField(value: unknown): value is ToolDetailField {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const field = value as Record<string, unknown>;
  if (!TOOL_DETAIL_FIELD_STATES.includes(field.state as ToolDetailFieldState)) {
    return false;
  }
  // Available must carry a `value` property, even when that value is null.
  return field.state !== "available" || Object.prototype.hasOwnProperty.call(field, "value");
}

/** Strictly validate the narrow normalized response accepted from the bridge. */
export function isToolDetailsResponse(value: unknown): value is ToolDetailsResponse {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const response = value as Record<string, unknown>;
  return (
    typeof response.message_id === "string" &&
    typeof response.tool_name === "string" &&
    isDetailField(response.parameters) &&
    isDetailField(response.result) &&
    isDetailField(response.error)
  );
}

export function detailSourceKey(source: ToolDetailSource): string {
  if (source.kind === "history") {
    return `${source.sessionId}:${source.messageId}`;
  }
  return source.key;
}
